import { Component, OnInit, ViewChild } from '@angular/core';
import { CustomRadioButtonComponent } from "../../../../../shared/components/custom-radio-button/custom-radio-button.component";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PrimaryButtonComponent } from "../../../../../shared/components/primary-button/primary-button.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { combineLatest, firstValueFrom } from 'rxjs';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { UserDataService } from '../../../../../core/services/user-data.service';
import { UserService } from '../../../../../core/api/user.service';
import { UserSettings } from '../../../../../core/interfaces/user-interfaces';
import { Router } from '@angular/router';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { HttpErrorResponse } from '@angular/common/http';

interface Option {
  id: number;
  name: string;
  checked: boolean;
}

@Component({
  selector: 'app-onboarding-step-three',
  standalone: true,
  imports: [CustomRadioButtonComponent, CommonModule, FormsModule, PrimaryButtonComponent, TranslocoPipe, ToastComponent],
  templateUrl: './onboarding-step-three.component.html',
  styleUrl: './onboarding-step-three.component.css'
})
export class OnboardingStepThreeComponent implements OnInit {
  options: Option[] = [
    // Energy Management
    { id: 0, name: `Cost Forecasting`, checked: false },
    { id: 1, name: `Monthly Invoice Check`, checked: false },
    { id: 2, name: `Create Budget Plans`, checked: false },
    { id: 3, name: `Cost Analysis`, checked: false },

    // Cost Optimization
    { id: 4, name: `Price Benchmarking and Contract Optimization`, checked: false },
    { id: 5, name: `Analyse Solar Potential`, checked: false },

    // ESG and Sustainability
    { id: 6, name: `ESG Reporting and Score Improvement`, checked: false },
    { id: 7, name: `Charge of Energy Mix`, checked: false },
    { id: 8, name: `Green Mobility`, checked: false },
  ];

  get selectAllButton(): boolean {
    return this.options.every(option => option.checked);
  }

  set selectAllButton(value: boolean) {
    this.options.forEach(option => (option.checked = value));
  }

  continueClicked: boolean = false;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private translocoService: TranslocoService, private userData: UserDataService, private userService: UserService, private router: Router, private apiError: ApiErrorService) { }

  ngOnInit(): void {
    // Dynamically populate option names with translated strings
    const optionKeys = [
      'auth.onboarding_step_3.cost_forecasting',
      'auth.onboarding_step_3.monthly_invoice_check',
      'auth.onboarding_step_3.create_budget_plans',
      'shared.cost_analysis',
      'auth.onboarding_step_3.price_benchmarking_and_contract_optimization',
      'auth.onboarding_step_3.analyse_solar_potential',
      'auth.onboarding_step_3.esg_reporting',
      'auth.onboarding_step_3.charge_of_energy_mix',
      'shared.green_mobility',
    ];

    combineLatest(optionKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.options = this.options.map((option, index) => ({
        ...option,
        name: translations[index],
      }));
    });
  }

  toggleSelectAll(): void {
    this.options.forEach(option => (option.checked = this.selectAllButton));
  }

  async continueButtonClick() {
    // Prevent multiple clicks
    if (this.continueClicked) {
      return;
    }
    this.continueClicked = true;

    // Filter checked options
    const checkedOptions: string[] = this.options
      .filter(option => option.checked === true)
      .map(option => option.name);

    if (!this.userData.getUser()) {
      await this.userData.loadUserData();
    }

    // Add new data to user settings
    let user = this.userData.getUser();
    if (!user?.settings) {
      this.toast.showToast("shared.error", "shared.toast.unexpected_error", 'error', 3000);
      this.continueClicked = false;
      return;
    }
    user.settings.workspace_preferences = checkedOptions;

    // Sending new data
    let updateUserSettingResponse = undefined;
    try {
      updateUserSettingResponse = await firstValueFrom(this.userService.updateUserSettings(user.settings.id, user.settings));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.continueClicked = false;
      return;
    }

    // Saving new user settings
    user.settings = updateUserSettingResponse as UserSettings;
    this.userData.setUser(user);

    this.router.navigate(["/auth/onboarding/"], { queryParams: { step: 4 } });
    this.continueClicked = false;
  }
}
